import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { IAuth } from '@modules/login/login.component';
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { NotificationDialogComponent } from '@shared/components/notification-dialog/notification-dialog.component';
import { TestTypeStorageService } from '@shared/services/test-type-storage/test-type-storage.service';
import { Router } from '@angular/router';
import { ApiService } from '@core/services/api.service';
import { AppConstants } from '@core/constants';
import { ITileConfig } from '@shared/components/interfaces/tile.interface';
import { TileTypeNavigationEnum } from '@shared/enums/tile-type-navigation.enum';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { CongratulationOnboardingDialogComponent } from '@shared/dialogs/congratulation-onboarding-dialog/congratulation-onboarding-dialog.component';
import { SagaStore } from '@features/onboarding/store/saga.store';

@Injectable({
  providedIn: 'root',
})
export class OnboardingService {
  private sagaStore = inject(SagaStore);

  private apiService: ApiService = inject(ApiService);
  private router: Router = inject(Router);
  private testTypeStorageService: TestTypeStorageService = inject(TestTypeStorageService);
  private dialog: MatDialog = inject(MatDialog);
  private isOnboardingComplete$: BehaviorSubject<any> = new BehaviorSubject<any>(true);
  private onboardingProgress$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  private snackBar: MatSnackBar = inject(MatSnackBar);

  public auth: IAuth;

  constructor() {}

  public setOnboardingStatus(status: boolean): void {
    this.isOnboardingComplete$.next(status);
  }

  public getOnboardingStatus(): BehaviorSubject<boolean> {
    return this.isOnboardingComplete$;
  }

  public setOnboardingPercentage(status: number): void {
    this.onboardingProgress$.next(status);
  }

  public getOnboardingPercentage(): BehaviorSubject<number> {
    return this.onboardingProgress$;
  }

  public openStepperModal(type: string, media: any): void {
    const dialogRef: MatDialogRef<NotificationDialogComponent> = this.dialog.open(NotificationDialogComponent, {
      panelClass: 'general-dialog',
      width: '900px',
      height: 'auto',
      autoFocus: false,
      data: {
        media,
      },
    });
    dialogRef.afterClosed().subscribe((response: any): void => {
      if (response) {
        this.testTypeStorageService.resetConfig();

        if (type === 'lactate') {
          this.testTypeStorageService.setTestConfig({
            isManual: false,
            isPPD: false,
            isVirtual: false,
            isLactate: true,
            testId: undefined,
            isBack: false,
          });
          this.router.navigate(['/home/lactate-test']);
        } else if (type === 'ppd') {
          this.testTypeStorageService.setTestConfig({
            isManual: false,
            isPPD: true,
            isVirtual: false,
            isLactate: false,
            testId: undefined,
            isBack: false,
          });
          this.router.navigate(['/home/ppd-test']);
        }
      }
    });
  }

  public updateSaga(sagaConfig: any, sagaId: number): Observable<any> {
    return this.apiService.put(`${AppConstants.API.ACCOUNTS.ONBOARDING.PUT_SAGA}${sagaId}/`, sagaConfig);
  }

  public getUseCases(): Observable<any> {
    return this.apiService.get(AppConstants.API.ACCOUNTS.ONBOARDING.GET_USE_CASES);
  }

  public getMockedTiles(): ITileConfig[] {
    return [
      {
        id: 1,
        image: 'https://inscyd-prod-public.s3.eu-central-2.amazonaws.com/features/visualization/Visualization.png',
        title: 'Amateur Triathlete',
        description: 'This athlete has balanced endurance and moderate sprint capacity.',
        description_html: '<p>This athlete has balanced endurance and moderate sprint capacity.</p>',
        isRecommended: false,
        buttonName: 'Use the example',
        urlNavigateTo: '/home/apc/setcard/74',
        tags: [],
        category_name: 'Triathlon',
        tagHeight: 32,
        marketingFeatures: ['Balanced Training', 'Moderate Intensity'],
        suggestedCourses: [],
        price: 0,
        currencyName: 'USD',
        navigationType: TileTypeNavigationEnum.APP,
        configuration: null,
        is_enabled: true,
        is_onboarding_track: true,
      },
      {
        id: 2,
        image: 'https://inscyd-prod-public.s3.eu-central-2.amazonaws.com/features/visualization/Visualization.png',
        title: 'Professional Cyclist',
        description: 'This athlete has a high VO2max and low VLamax for endurance running.',
        description_html: '<p>This athlete has a high VO2max and low VLamax for endurance running.</p>',
        isRecommended: false,
        buttonName: 'Use the example',
        urlNavigateTo: '/home/apc/setcard/74',
        tags: [],
        category_name: 'Cycling',
        tagHeight: 32,
        marketingFeatures: ['Professional Training', 'High Performance'],
        suggestedCourses: [],
        price: 0,
        currencyName: 'USD',
        navigationType: TileTypeNavigationEnum.APP,
        configuration: null,
        is_enabled: true,
        is_onboarding_track: true,
      },
      {
        id: 3,
        image: 'https://inscyd-prod-public.s3.eu-central-2.amazonaws.com/features/visualization/Visualization.png',
        title: 'Masters Age Group Cyclist',
        description: 'This athlete has strong aerobic but shows anaerobic decline with age.',
        description_html: '<p>This athlete has strong aerobic but shows anaerobic decline with age.</p>',
        isRecommended: false,
        buttonName: 'Use the example',
        urlNavigateTo: '/home/apc/setcard/74',
        tags: [],
        category_name: 'Cycling',
        tagHeight: 32,
        marketingFeatures: ['Age Group Training', 'Aerobic Focus'],
        suggestedCourses: [],
        price: 0,
        currencyName: 'USD',
        navigationType: TileTypeNavigationEnum.APP,
        configuration: null,
        is_enabled: true,
        is_onboarding_track: true,
      },
    ];
  }

  public setSagaHistory(step: string, percentage: number): void {
    const currentSagaData: string | null = localStorage.getItem('saga_config');

    if (currentSagaData) {
      const sagaConfig: any = JSON.parse(currentSagaData);
      let sagaData = sagaConfig.saga_data ?? [];
      sagaData.push({ step, percentage });
      sagaConfig.saga_data = sagaData;
      sagaConfig.current_step = 2;
      localStorage.setItem('saga_config', JSON.stringify(sagaConfig));
    }
  }

  public getLocalStorageSagaConfig(): any {
    const sagaConfigString: string | null = localStorage.getItem('saga_config');
    let sagaConfig: any = {};

    if (sagaConfigString) {
      sagaConfig = JSON.parse(sagaConfigString);
    }

    return sagaConfig;
  }

  public setLocalStorageSagaConfig(sagaData: any): any {
    localStorage.setItem('saga_config', JSON.stringify(sagaData));
  }

  /**
   * saga_data is: [{step:string, percentage: int}] list of objects
   */
  public hasSagaDataStep(step: string): boolean {
    const sagaData: any = this.getLocalStorageSagaConfig();
    let hasStep: boolean = false;

    if (sagaData.hasOwnProperty('saga_data')) {
      sagaData.saga_data.forEach((item: any) => {
        if (item.step === step) {
          hasStep = true;
        }
      });
    }

    return hasStep;
  }

  public setStepForSaga(step: string, percentage: number): void {
    this.setSagaHistory(step, percentage);
    const sagaConfig: any = this.getLocalStorageSagaConfig();

    if (percentage === 100) {
      sagaConfig.current_step = 4; // Completed
    }

    if (sagaConfig.hasOwnProperty('guid') && sagaConfig.guid) {
      this.sagaStore.updateSaga(sagaConfig);
    } else {
      this.snackBar.open('Onboarding error, GUID is missing', 'OK', AppConstants.TOAST_CONFIG.ERROR);
    }
  }

  /** Deprecated */
  public getCurrentPercentage(): number {
    const sagaConfig: any = this.getLocalStorageSagaConfig();

    return sagaConfig.hasOwnProperty('saga_data')
      ? sagaConfig.saga_data[sagaConfig.saga_data.length - 1].percentage
      : 0;
  }

  public congratulationOnboardingDialog(): void {
    const dialogRef: MatDialogRef<CongratulationOnboardingDialogComponent> = this.dialog.open(
      CongratulationOnboardingDialogComponent,
      {
        width: '400px',
        data: {
          title: 'Congratulations - you have just created the first 360° metabolic profile in INSCYD',
          message:
            'What would you like to do next: do a quick tour through the whole metabolic profile. Or explore on your own (there are explanations for each graph and element - marked with an “i” icon',
        },
      }
    );

    dialogRef.afterClosed().subscribe((result: any): void => {
      this.setOnboardingStatus(true);
      if (result === 'home') {
        this.router.navigate(['/home/dashboard']);
      } else if (result === 'another') {
        this.router.navigate(['/home/onboarding/use-cases']);
      }
    });
  }
}
